import {
  createContext,
  useContext,
  FunctionComponent,
  ComponentType,
  ReactNode,
  useMemo,
  useState,
  useEffect,
  Context
} from 'react';
import { createPortal } from 'react-dom';
import { ThemeProvider, StyleSheetManager } from 'styled-components';

import { GlobalStyle } from '../../styles';
import {
  ThemeMachine,
  BaseThemeMachine,
  DefaultSettableTheme,
  DefaultThemeDefinition
} from '../../theme';
import {
  createTranslationPack,
  Translation,
  TranslationPack,
  DefaultTranslation,
  direction as directionOfLocale
} from '../../i18n';
import { FileInputProps } from '../File';
import { windowIsAvailable, navigatorIsAvailable, documentIsAvailable } from '../../utils';

export interface ConfigurationProps {
  /**
   * Any components or nodes that should be impacted by the settings applied by this Configuration component.
   */
  children: ReactNode;
  /**
   * Context to read previous configuration values from.
   */
  context?: Context<ConfigurationContextValue>;
  /**
   * User locale as defined in [BCP-47](https://www.techonthenet.com/js/language_tags.php).
   */
  locale?: string;
  /**
   * Override rendering direction of the document. Direction will be based on the locale if now provided.
   */
  direction?: 'ltr' | 'rtl';
  /**
   * Object with (partial) translations.
   */
  translations?: Translation;
  /**
   * Theme object used to override any or all Cosmos theme properties.
   */
  theme?: DefaultSettableTheme;
  /**
   * Disables loading Open Sans from Google Fonts. Only takes effect on the root configuration.
   */
  disableDefaultFontLoading?: boolean;
  /**
   * Target element for loading styles related to `styled-components`.
   */
  styleSheetTarget?: HTMLElement;
  /**
   * Target element for rendering a `ReactDOM` portal.
   */
  portalTarget?: Element;
  /**
   * Override map to replace Cosmos components.
   */
  overrideMap?: {
    FileInput?: ComponentType<FileInputProps>;
  };
  /**
   * Use native HTML5 controls for inputs instead of rich ones.
   * @default false
   */
  renderNativeControls?: boolean;
}

const initializedKey = Symbol.for('@pega/cosmos-react-core.configuration.initialized');

export interface ConfigurationContextValue {
  [initializedKey]: boolean;
  locale: string;
  direction: ConfigurationProps['direction'];
  translations: TranslationPack;
  themeMachine: ThemeMachine<DefaultThemeDefinition>;
  styleSheetTarget?: HTMLElement;
  portalTarget?: Element;
  overrideMap: NonNullable<ConfigurationProps['overrideMap']>;
  renderNativeControls: boolean;
}

const defaultProps: Partial<ConfigurationProps> = {};
const defaultLocale = navigatorIsAvailable ? navigator.language : 'en';

export const ConfigurationContext = createContext<ConfigurationContextValue>({
  [initializedKey]: false,
  locale: defaultLocale,
  direction: directionOfLocale(defaultLocale),
  translations: DefaultTranslation,
  themeMachine: BaseThemeMachine,
  styleSheetTarget: documentIsAvailable ? document.head : undefined,
  portalTarget: documentIsAvailable ? document.body : undefined,
  overrideMap: {},
  renderNativeControls: false
});

const metaKey = Symbol.for('@pega/cosmos-react-core.metadata');

interface CosmosMetadata {
  version: string;
}

declare global {
  interface Window {
    [metaKey]: CosmosMetadata[];
  }
}

if (windowIsAvailable) {
  if (!window[metaKey]) {
    window[metaKey] = [];
  }

  window[metaKey].push({ version: '2.1.4' });
}

const Configuration: FunctionComponent<ConfigurationProps> = ({
  context = ConfigurationContext,
  children,
  locale,
  direction,
  translations: customTranslations,
  theme,
  disableDefaultFontLoading,
  styleSheetTarget,
  portalTarget,
  overrideMap,
  renderNativeControls
}: ConfigurationProps) => {
  const ctx = useContext(context);
  const themeMachine = theme
    ? new ThemeMachine<DefaultThemeDefinition>({ theme, parent: ctx.themeMachine })
    : ctx.themeMachine;
  const translations = useMemo(
    () => createTranslationPack({ ...ctx.translations, ...customTranslations }),
    [ctx.translations, customTranslations]
  );

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const wrappedChildren = ctx[initializedKey] ? (
    children
  ) : (
    <StyleSheetManager disableVendorPrefixes target={styleSheetTarget ?? ctx.styleSheetTarget}>
      <>
        {!disableDefaultFontLoading &&
          mounted &&
          documentIsAvailable &&
          createPortal(
            <link
              rel='stylesheet'
              href='https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,400;0,600;0,700;1,400;1,600;1,700&display=swap'
            />,
            document.head
          )}
        <GlobalStyle />
        {children}
      </>
    </StyleSheetManager>
  );
  return (
    <ConfigurationContext.Provider
      value={{
        locale: locale ?? ctx.locale,
        direction: direction ?? (locale !== undefined ? directionOfLocale(locale) : ctx.direction),
        translations,
        themeMachine,
        [initializedKey]: true,
        styleSheetTarget: styleSheetTarget ?? ctx.styleSheetTarget,
        portalTarget: portalTarget ?? ctx.portalTarget,
        overrideMap: overrideMap ? { ...ctx.overrideMap, ...overrideMap } : ctx.overrideMap,
        renderNativeControls: renderNativeControls ?? ctx.renderNativeControls
      }}
    >
      <ThemeProvider theme={themeMachine.theme}>{wrappedChildren}</ThemeProvider>
    </ConfigurationContext.Provider>
  );
};

Configuration.defaultProps = defaultProps;

export default Configuration;
